import { connectStreamSource, disconnectStreamSource } from "@hotwired/turbo"
import consumer from "../channels/consumer"

// <turbo-cable-stream-source> propio en lugar del de turbo-rails (se importa antes que la librería, que solo define
// el suyo si no existe). Doc: _docs/frontend/cable_stream_sources.md

// Suscripciones vivas por identificador (el mismo JSON con el que Action Cable identifica una suscripción).
export class StreamSubscriptions {
  constructor(subscribe, defer = callback => setTimeout(callback, 0)) {
    this.subscribe = subscribe
    this.defer = defer
    this.entries = new Map()
    this.entryBySource = new Map()
  }

  // Registra la fuente en la suscripción viva de su stream, o la crea
  add(source) {
    const identifier = JSON.stringify(source.channel)
    let entry = this.entries.get(identifier)
    if (!entry) {
      entry = { identifier, sources: new Set(), connected: false, subscription: null }
      this.entries.set(identifier, entry)
      entry.subscription = this.subscribe(source.channel, {
        received: data => entry.sources.forEach(listener => listener.dispatchMessageEvent(data)),
        connected: () => { entry.connected = true; entry.sources.forEach(listener => listener.subscriptionConnected()) },
        disconnected: () => { entry.connected = false; entry.sources.forEach(listener => listener.subscriptionDisconnected()) }
      })
    }
    entry.sources.add(source)
    this.entryBySource.set(source, entry)
    if (entry.connected) source.subscriptionConnected()
  }

  // Saca la fuente ahora; la baja de la suscripción se decide al final del tick
  remove(source) {
    const entry = this.entryBySource.get(source)
    if (!entry) return
    this.entryBySource.delete(source)
    entry.sources.delete(source)
    source.subscriptionDisconnected()
    this.defer(() => this.release(entry))
  }

  // Baja la suscripción solo si nadie la escucha y sigue siendo la viva de su stream
  release(entry) {
    if (entry.sources.size > 0 || this.entries.get(entry.identifier) !== entry) return
    this.entries.delete(entry.identifier)
    entry.subscription.unsubscribe()
  }
}

// Claves data-* a snake_case como parámetros extra del canal, igual que turbo-rails
function snakeize(dataset) {
  return Object.fromEntries(
    Object.entries(dataset).map(([key, value]) => [key.replace(/[A-Z]+/g, letters => `_${letters.toLowerCase()}`), value])
  )
}

// La conexión de cable de la app (channels/consumer): así los streams de Turbo no abren una segunda conexión
const streamSubscriptions = new StreamSubscriptions((channel, mixin) => consumer.subscriptions.create(channel, mixin))

class CableStreamSourceElement extends HTMLElement {
  static observedAttributes = ["channel", "signed-stream-name"]

  connectedCallback() {
    connectStreamSource(this)
    streamSubscriptions.add(this)
  }

  disconnectedCallback() {
    disconnectStreamSource(this)
    streamSubscriptions.remove(this)
  }

  // Cambió el stream de una fuente ya en el documento: libera el viejo y pide el nuevo
  attributeChangedCallback() {
    if (!this.isConnected) return
    this.disconnectedCallback()
    this.connectedCallback()
  }

  // Turbo escucha "message" en cada fuente conectada y renderiza el stream. Mismo nombre que en turbo-rails:
  // delay_cable (test/system/storefront_quick_add_test.rb) lo parchea en la instancia para atrasar el cable
  dispatchMessageEvent(data) {
    this.dispatchEvent(new MessageEvent("message", { data }))
  }

  subscriptionConnected() {
    this.setAttribute("connected", "")
  }

  subscriptionDisconnected() {
    this.removeAttribute("connected")
  }

  get channel() {
    const channel = this.getAttribute("channel")
    const signed_stream_name = this.getAttribute("signed-stream-name")
    return { channel, signed_stream_name, ...snakeize(this.dataset) }
  }
}

if (customElements.get("turbo-cable-stream-source") === undefined) {
  customElements.define("turbo-cable-stream-source", CableStreamSourceElement)
} else {
  console.warn("turbo-cable-stream-source ya estaba definido: cable_stream_source_element debe importarse antes de @hotwired/turbo-rails")
}
